import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Crown } from 'iconoir-react'
import Button from 'app/components/Button'
import { FormPanelShadcnComponent } from 'app/components/form-panel/FormPanelShadcnComponent'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { showModal } from 'app/lib/modal'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useManagedNumbers } from './useManagedNumbers'
import { PurchaseNumberForm } from './PurchaseNumberModal'
import { PHONE_NUMBER_ROLES } from 'app/types/phone-number'

const STATUS_STYLES: Record<string, { dot: string; label: string }> = {
  active: { dot: 'bg-green-500', label: 'Active' },
  ordering: { dot: 'bg-yellow-500', label: 'Ordering...' },
  failed: { dot: 'bg-red-500', label: 'Failed' },
}

const PROVIDER_LABELS: Record<string, string> = {
  'aws-chime': 'Voice',
  'aws-messaging': 'Messaging',
  twilio: 'Legacy (Twilio)',
}

function RoleEditor({
  roles,
  disabled,
  onSave,
}: {
  roles: string[]
  disabled?: boolean
  onSave: (roles: string[]) => void
}) {
  const [editing, setEditing] = useState<Boolean>(false)
  const [draft, setDraft] = useState<string[]>(roles)

  const handleToggle = (role: string) => {
    setDraft((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]))
  }

  const handleSave = () => {
    onSave(draft)
    setEditing(false)
  }

  if (!editing) {
    return (
      <Button
        label={roles.join(', ')}
        mode="secondary"
        size="sm"
        onClick={() => {
          setDraft(roles)
          setEditing(true)
        }}
        disabled={disabled}
        aria-label="Edit roles"
      />
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PHONE_NUMBER_ROLES.map((role) => (
        <label key={role} className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={draft.includes(role)} onChange={() => handleToggle(role)} />
          {role}
        </label>
      ))}
      <Button label="Save" mode="primary" size="sm" onClick={handleSave} />
      <Button label="Cancel" mode="secondary" size="sm" onClick={() => setEditing(false)} />
    </div>
  )
}

export const ManagedNumbersPanel = observer(function ManagedNumbersPanel({ locationId }: { locationId: string }) {
  const { phoneNumbers, fetching, error, releaseNumber, updateRoles, refetch } = useManagedNumbers(locationId)
  const [confirm] = useConfirmation()

  const handlePurchase = () => {
    showModal(<PurchaseNumberForm locationId={locationId} onSuccess={refetch} />, {
      title: `Purchase Phone Number`,
      isFullScreen: false,
      useRadix: true,
    })
  }

  const handleRelease = (id: string, phone: string) => {
    confirm({
      variant: 'danger',
      title: 'Release Number',
      description: `This will release ${formatPhoneNumber(phone) || phone} and return it to the provider. This cannot be undone.`,
    }).then(() => releaseNumber(id))
  }

  const titleWithCrown = (
    <span className="flex items-center gap-1.5">
      Managed Numbers
      <Crown width={14} height={14} className="text-amber-800" />
    </span>
  )

  return (
    <FormPanelShadcnComponent title={titleWithCrown} cols={6}>
      <div className="flex flex-col gap-y-4">
        <div className="flex items-center justify-between">
          <p className="text-body-sm text-minimal">Phone numbers purchased and managed by Trackable.</p>
          <Button label="Purchase Number" mode="primary" size="sm" onClick={handlePurchase} />
        </div>

        {fetching && <p className="text-body-sm text-minimal">Loading...</p>}

        {error && <p className="text-body-sm text-red-600">Failed to load numbers: {error}</p>}

        {!fetching && !error && phoneNumbers.length === 0 && (
          <p className="text-body-sm text-minimal">No managed numbers for this location.</p>
        )}

        {phoneNumbers.map((pn) => {
          const statusStyle = STATUS_STYLES[pn.provisioningStatus] ?? STATUS_STYLES.active
          return (
            <div key={pn.id} className="flex items-center gap-3 rounded-lg border border-default bg-surface px-4 py-3">
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-body-sm font-medium text-subtle">
                    {formatPhoneNumber(pn.phone) || pn.phone}
                  </span>
                  <span className="rounded bg-surface-muted px-2 py-0.5 text-xs text-subtle">
                    {PROVIDER_LABELS[pn.provider] ?? pn.provider}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <RoleEditor
                    roles={pn.roles}
                    onSave={(newRoles) => updateRoles(pn.id, newRoles)}
                    disabled={pn.provisioningStatus !== 'active'}
                  />
                  <span className="flex items-center gap-1 text-xs text-minimal">
                    <span className={`inline-block h-2 w-2 rounded-full ${statusStyle.dot}`} />
                    {statusStyle.label}
                  </span>
                  {pn.provisioningError && <span className="text-xs text-red-600">{pn.provisioningError}</span>}
                  {pn.registrationStatus && (
                    <span className="text-xs text-minimal">Registration: {pn.registrationStatus}</span>
                  )}
                </div>
              </div>
              <Button
                label="Release"
                mode="danger"
                size="sm"
                onClick={() => handleRelease(pn.id, pn.phone)}
                disabled={pn.provisioningStatus === 'ordering'}
              />
            </div>
          )
        })}
      </div>
    </FormPanelShadcnComponent>
  )
})
